/* eslint-disable react/prop-types */
import * as React from 'react';

import { LicenseTransactionTypes } from '@showbie-socrative/socrative-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ContactSalesEventAdapter } from '~analytics/adapters/ContactSalesEventAdapter';
import { translate } from '~l10n/client';

import typeform from '~shared/helpers/typeform';
import { AccountPage } from '~teacher/pages/AccountPage';
import { queryClient } from '~teacher/queryClient';
import Constants from 'Constants';
import DownloadIcon from 'DownloadIcon';
import EmailIcon from 'EmailIcon';
import LicenseView from 'LicenseView';
import platform from 'Platform';
import ProfileTabs from 'ProfileTabs';
import ProIcon from 'ProIcon';
import RenewProIcon from 'RenewProIcon';
import Toggle from 'Toggle';

const PurchaseOrContactButton = ({ maxDiscount, controller, model }) => {
  const isPurchaseButton =
    model.customerType !== LicenseTransactionTypes.Sales &&
    (!platform.isAndroidApp && !platform.isIosApp) === true;

  return (
    <div
      style={{
        background: '#f3f3f3',
        padding: '1.5em',
        borderRadius: '8px',
        margin: '20px 0 40px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '16px',
          marginBottom: '10px',
        }}
      >
        {translate('Purchase Pro for yourself or a group', true)}
      </div>

      {isPurchaseButton && maxDiscount > 0 && (
        <div>
          {translate(
            'Save up to {0}% when you purchase multiple seats!'
          ).format(maxDiscount)}
        </div>
      )}

      {
        <button
          style={{
            marginTop: '20px',
          }}
          className={'action-button action-button-200'}
          onClick={
            isPurchaseButton
              ? controller.upgradeClicked
              : () => {
                  new ContactSalesEventAdapter({
                    form_source: 'typeform',
                  }).sendAnalytics();

                  window
                    .open(
                      typeform({
                        medium: 'contactus_text',
                        ...model,
                      }),
                      '_blank'
                    )
                    .focus();
                }
          }
        >
          {isPurchaseButton
            ? translate('Get Pro', true)
            : translate('Contact Sales', true)}
        </button>
      }
    </div>
  );
};

export default class AccountView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      upgrading: false,
    };
  }

  render() {
    let c = this.props.controller;
    let m = this.props.model;
    let l = m.licenseModel;
    let licenseChildren = [];
    const showLicenseView = this.props.model.showLicenseView;

    let maxDiscount = 0;

    let renewalChildren = [
      <div key="renewalChildren-div-1" className="account-icon-container">
        {React.createElement(
          (c.inRenewalWindow() && !m.autoRenew) || this.props.hasRecentlyExpired
            ? RenewProIcon
            : ProIcon
        )}
      </div>,
    ];

    let expiredHeader = '';

    if (this.props.hasRecentlyExpired) {
      expiredHeader = translate('Your license has expired!');
    }

    if (!(m.buyingLicense || l.licenseStep === Constants.COMPLETE_STEP)) {
      renewalChildren.push(
        <span key="renewalChildren-span-1" className="license-expired-header">
          {expiredHeader}
        </span>,
        <div key="enter-license-key-div" className="activate-link-container">
          <span className="link" onClick={c.enterLicenseKeyClicked}>
            {translate('Enter License Key')}
          </span>
        </div>,
        <div
          key="renewalChildren-div-2"
          className="account-summary-row first-account-summary-row"
        >
          <span className="account-summary-left-column">
            {translate('License Key')}
          </span>
          <span className="account-summary-right-column license-key-right-column">
            {m.licenseKey}
          </span>
        </div>,
        <div key="renewalChildren-div-3" className="account-summary-row">
          <span className="account-summary-left-column">
            {translate('Expiration Date')}
          </span>
          <span className="account-summary-right-column">
            {this.props.expiration}
          </span>
        </div>
      );

      if (m.canAutoRenew && !this.props.hasRecentlyExpired) {
        renewalChildren.push(
          <div key="renewalChildren-div-4" className="account-summary-row">
            <span className="account-summary-left-column">
              {translate('Auto Renew')}
            </span>
            <Toggle
              className="auto-renew-toggle"
              checked={m.autoRenew}
              id="toggleAutoRenew"
              onChange={c.accountAutoRenewChanged}
            />
          </div>
        );
      }

      renewalChildren.push(
        <PurchaseOrContactButton
          key="renwalChildren-div-5"
          model={m}
          controller={c}
          maxDiscount={maxDiscount}
        />
      );
    }

    licenseChildren.push(
      <div key="licenseChildren-div-1" className="renewal-container">
        {renewalChildren}
      </div>
    );

    if (m.buyingLicense) {
      licenseChildren.push(
        <LicenseView key="licenseChildren-LicenseView-1" {...this.props} />
      );
    }

    let handleReceipt = [];

    if (!m.buyingLicense && m.isBuyer) {
      handleReceipt.push(
        <div
          key="handleReceipt-div-1"
          className={
            platform.isIE() || platform.isEdge()
              ? 'fixed-width view-receipt-container'
              : 'view-receipt-container'
          }
        >
          <p>{translate('Need a copy of your receipt?')}</p>
          <div
            className={
              platform.isMobile()
                ? 'receipt-buttons-container clearfix'
                : 'hover receipt-buttons-container clearfix'
            }
          >
            <span
              className="receipt-button email-button"
              onClick={c.emailReceiptClicked}
            >
              <EmailIcon />
              <span className="receipt-email-label">
                {translate('Email', true)}
              </span>
            </span>
            <span
              className={
                platform.isIos
                  ? 'receipt-button download-button disabled'
                  : 'receipt-button download-button'
              }
              onClick={c.downloadReceiptClicked}
            >
              <DownloadIcon />
              <span className="receipt-download-label">
                {translate('Download', true)}
              </span>
            </span>
          </div>
        </div>
      );
    }

    return (
      <div
        id="profile-container"
        className="max-w-7xl m-auto flex flex-col gap-6"
      >
        <ProfileTabs {...this.props} />
        <div
          id="profile-form-container"
          className={showLicenseView ? 'form' : ''}
        >
          {showLicenseView ? (
            <>
              {licenseChildren}
              {handleReceipt}
            </>
          ) : (
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <AccountPage />
            </QueryClientProvider>
          )}
        </div>
      </div>
    );
  }
}
